import React, { useEffect, useState } from 'react';
import {
  Backdrop, Box, CircularProgress, Snackbar, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import MuiAlert from '@mui/material/Alert';
import ResponsiveAppBar from '../ResponsiveAppBar.js';
import ResponsiveFooter from '../ResponsiveFooter.js';
import { WorkspaceProvider, useWorkspace } from '../workspace/WorkspaceContext.js';
import WorkspaceSidebar, { MENUS } from '../workspace/WorkspaceSidebar.js';
import WorkspacePanel from '../workspace/WorkspacePanel.js';
import { PANEL_WIDTH, SIDEBAR_WIDTH } from '../workspace/workspaceConstants.js';
import WorkspaceMap from '../workspace/WorkspaceMap.js';
import Workspace3DMap from '../workspace/Workspace3DMap.js';
import MapLegend from '../workspace/MapLegend.js';
import MapTitleChip from '../workspace/MapTitleChip.js';
import ActionPropertiesDialog from '../workspace/ActionPropertiesDialog.js';
import BuildingsPanel from '../workspace/panels/BuildingsPanel.js';
import ScenariosPanel from '../workspace/panels/ScenariosPanel.js';
import ActionsPanel from '../workspace/panels/ActionsPanel.js';
import ViewPanel from '../workspace/panels/ViewPanel.js';
import SharePanel from '../workspace/panels/SharePanel.js';
import '../workspace/workspace.css';

const PANEL_CONTENT = {
  buildings: BuildingsPanel,
  scenarios: ScenariosPanel,
  actions: ActionsPanel,
  view: ViewPanel,
  share: SharePanel,
};

const WorkspaceBody = () => {
  const { dataLoaded, openMenu, setOpenMenu, interactionMode, exporting, toast, dismissToast } = useWorkspace();

  // The panel keeps rendering the menu it last showed, so it still has content
  // while it slides back out.
  const [shownMenu, setShownMenu] = useState(null);
  useEffect(() => {
    if (openMenu) setShownMenu(openMenu);
  }, [openMenu]);

  const PanelContent = shownMenu ? PANEL_CONTENT[shownMenu] : null;
  const panelTitle = shownMenu ? MENUS.find((menu) => menu.id === shownMenu).label : '';
  const erasing = interactionMode === 'erase-buildings' || interactionMode === 'erase-actions';

  // The 3D view is read-only, so starting to draw or erase
  // anything drops back to the 2D map.
  const [view3d, setView3d] = useState(false);
  useEffect(() => {
    if (interactionMode) setView3d(false);
  }, [interactionMode]);

  return (
    <div
      className="workspace"
      style={{
        '--workspace-sidebar-width': `${SIDEBAR_WIDTH}px`,
        '--workspace-panel-width': `${PANEL_WIDTH}px`,
      }}
    >
      {/* Header and map together are exactly one screen tall; the footer sits
          below the fold and is reached by scrolling. */}
      <div className="workspace-viewport">
        <ResponsiveAppBar />

        <div className="workspace-body">
          <WorkspaceSidebar />

          <div className={`workspace-panel-host${openMenu ? ' workspace-panel-host--open' : ''}`}>
            {PanelContent && (
              <WorkspacePanel title={panelTitle} onClose={() => setOpenMenu(null)}>
                <PanelContent />
              </WorkspacePanel>
            )}
          </div>

          <div className={`workspace-map${erasing ? ' workspace-map--erase' : ''}${openMenu ? ' workspace-map--panel-open' : ''}`}>
            {dataLoaded ? (
              <>
                {view3d ? <Workspace3DMap /> : <WorkspaceMap />}
                <MapTitleChip />
                <MapLegend />
                <Box sx={{
                  position: 'absolute', bottom: '48px', zIndex: 900,
                  left: openMenu ? `${PANEL_WIDTH + 12}px` : '12px',
                  transition: 'left 260ms cubic-bezier(0.4, 0, 0.2, 1)',
                  background: 'rgba(255,255,255,0.92)', borderRadius: '5px',
                }}>
                  <Tooltip title={interactionMode ? 'Zeichnen und Löschen nur in 2D' : 'Zwischen 2D- und 3D-Ansicht wechseln'}>
                    {/* Wrapped, since a Tooltip gets no events from a disabled child. */}
                    <span>
                      <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={view3d ? '3d' : '2d'}
                        onChange={(e, value) => value && setView3d(value === '3d')}
                        disabled={Boolean(interactionMode)}
                      >
                        <ToggleButton value="2d" sx={{ px: 1.5, fontWeight: 700 }}>2D</ToggleButton>
                        <ToggleButton value="3d" sx={{ px: 1.5, fontWeight: 700 }}>3D</ToggleButton>
                      </ToggleButtonGroup>
                    </span>
                  </Tooltip>
                </Box>
              </>
            ) : (
              <Box sx={{
                height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexDirection: 'column', gap: 2,
              }}>
                <CircularProgress />
                <Typography variant="body2">Lade…</Typography>
              </Box>
            )}
          </div>
        </div>
      </div>

      <ResponsiveFooter />

      <ActionPropertiesDialog />

      <Backdrop open={exporting} sx={{ zIndex: 9999, flexDirection: 'column', gap: 2 }}>
        <CircularProgress sx={{ color: '#fff' }} />
        <Typography variant="h6" sx={{ color: '#fff' }}>
          Ihre Gefahrenkarte wird erstellt…
        </Typography>
      </Backdrop>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={6000}
        onClose={dismissToast}
        key={toast ? toast.key : undefined}
      >
        <MuiAlert elevation={6} variant="filled" onClose={dismissToast} severity={toast ? toast.severity : 'info'}>
          {toast ? toast.message : ''}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

// The single view a user works in for a whole region: the map fills everything
// between header and footer, and each menu of the left bar opens its own panel
// beside it. Replaces the former region overview, buildings, scenarios,
// scenario details, rain settings, computation, hazard map and sharing pages.
const RegionWorkspace = () => (
  <WorkspaceProvider>
    <WorkspaceBody />
  </WorkspaceProvider>
);

export default RegionWorkspace;
